import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@material-ui/core";

import { Close as CloseIcon } from "@material-ui/icons";

import { useMemo } from "react";

import {
  calcularRepartoCategorias,
  etiquetaNaturaleza,
  formatearMonto,
  getBancoConfig,
  formatearRutDisplay,
  naturalezaDeLinea,
  NATURALEZA_LINEA,
  TOLERANCIA_REPARTO,
} from "src/utils/conciliacionHelpers";

import { makeStyles } from "@material-ui/styles";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import { formatearFechaDisplay } from "src/utils/formatTime";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  value: {
    color: theme.palette.text.primary,
  },
}));

// ==================== CONFIGURACIONES ====================
const getTipoConciliacionConfig = (tipo) => {
  const configs = {
    AUTOMATICA: { label: "Automática", color: "#4CAF50", bgcolor: "#E8F5E9" },
    MANUAL: { label: "Manual", color: "#2196F3", bgcolor: "#E3F2FD" },
    PARCIAL: { label: "Parcial", color: "#FF9800", bgcolor: "#FFF3E0" },
  };
  return configs[tipo] || configs.MANUAL;
};

const getEstadoConciliacionConfig = (estado) => {
  const configs = {
    CONCILIADO: { label: "Conciliado", color: "success" },
    REVERTIDO: { label: "Revertido", color: "error" },
  };
  return configs[estado] || configs.CONCILIADO;
};

const getTipoAsignacionConfig = (tipo) => {
  const configs = {
    EXACTO: { label: "Exacto", color: "success" },
    PARCIAL: { label: "Parcial", color: "warning" },
  };
  return configs[tipo] || configs.EXACTO;
};

// Paleta del bloque de categorización contable. El color, el icono y la
// etiqueta salían de inferir un signo único para toda la conciliación
// comparando la suma de facturas contra el monto del movimiento. Ese signo hoy
// lo declara cada línea, y dos líneas del mismo movimiento pueden tenerlo
// opuesto, así que el bloque no puede afirmar uno solo: queda neutro y el signo
// se lee en la insignia de cada línea.
const ESTILO_CATEGORIZACION = {
  color: "#455A64",
  bgColor: "#ECEFF1",
  borderColor: "#B0BEC5",
};

// Colores de la insignia de naturaleza. Verde para el dinero que llegó con el
// depósito y ámbar para el que se retuvo antes, de modo que dos líneas con
// signos opuestos se distingan sin tener que leer el texto de cada una.
const ESTILO_NATURALEZA = {
  [NATURALEZA_LINEA.ADITIVA]: { color: "#2E7D32", bgColor: "#E8F5E9" },
  [NATURALEZA_LINEA.SUSTRACTIVA]: { color: "#EF6C00", bgColor: "#FFF3E0" },
};

const estiloNaturaleza = (naturaleza) =>
  ESTILO_NATURALEZA[naturaleza] || ESTILO_NATURALEZA[NATURALEZA_LINEA.ADITIVA];

// Qué se hizo con el abono que dejó el remanente. Es lo único de esta zona que
// el desglose no puede deducir de las líneas, porque ocurre después de guardar.
const DESTINO_ABONO = {
  APLICADO: "ya se aplicó a una factura",
  REEMBOLSADO: "se reembolsó al cliente",
  DESCARTADO: "se descartó",
};

export default function ModalDetalleConciliacion({
  open,
  onClose,
  conciliacion,
}) {
  const classes = useStyles();

  // Validación de datos
  const esConciliacionValida = useMemo(() => {
    return conciliacion && conciliacion.movimiento;
  }, [conciliacion]);

  // Líneas de categorización contable de esta conciliación (N líneas)
  const categorias = useMemo(
    () => conciliacion.categorias || [],
    [conciliacion.categorias],
  );

  // Facturas del reparto original. Aplicar un abono agrega después otra fila a
  // los detalles, marcada con es_abono, que no repartió el movimiento sino el
  // remanente que había quedado: contarla acá sumaría dos veces el mismo dinero
  // y el desglose dejaría de cuadrar.
  const detallesReparto = useMemo(
    () => (conciliacion.detalles || []).filter((det) => !det.es_abono),
    [conciliacion.detalles],
  );

  const sumaFacturasReparto = useMemo(
    () =>
      detallesReparto.reduce(
        (sum, det) => sum + (parseFloat(det.monto_asignado) || 0),
        0,
      ),
    [detallesReparto],
  );

  // Subtotales por naturaleza y remanente, con la misma fórmula que aplicó el
  // backend al guardar. Sumar las líneas sin mirar su signo devuelve una cifra
  // que no corresponde a ningún hecho cuando conviven los dos.
  const reparto = useMemo(
    () =>
      calcularRepartoCategorias({
        montoMovimiento: conciliacion.movimiento?.monto,
        sumaFacturas: sumaFacturasReparto,
        lineas: categorias,
      }),
    [conciliacion.movimiento, sumaFacturasReparto, categorias],
  );

  // Detectar si hay ajuste contable
  const tieneAjusteContable = useMemo(
    () => categorias.length > 0,
    [categorias],
  );

  // Detectar si es una retención completa (sin facturas, solo categorías)
  const esRetencionCompleta = useMemo(() => {
    return (
      tieneAjusteContable &&
      (!conciliacion.detalles || conciliacion.detalles.length === 0)
    );
  }, [tieneAjusteContable, conciliacion.detalles]);

  const tipoConfig = getTipoConciliacionConfig(conciliacion.tipo);
  const estadoConfig = getEstadoConciliacionConfig(conciliacion.estado);
  const bancoConfig = getBancoConfig(conciliacion.movimiento?.banco);

  // Calcular totales
  const totalAsignado = useMemo(() => {
    return (
      conciliacion.detalles?.reduce(
        (sum, det) => sum + parseFloat(det.monto_asignado || 0),
        0,
      ) || 0
    );
  }, [conciliacion.detalles]);

  const diferencia = useMemo(() => {
    const montoMovimiento = parseFloat(conciliacion.movimiento?.monto || 0);
    return montoMovimiento - totalAsignado;
  }, [conciliacion.movimiento, totalAsignado]);

  // El desglose cierra con el remanente calculado y no con el abono guardado:
  // si esa última fila viniera de otra fuente, la columna podría no cuadrar en
  // pantalla, que es justo lo que el formato existe para evitar. Estas notas
  // cubren lo que el cálculo no puede saber por sí solo.
  const notasAbono = useMemo(() => {
    if (!tieneAjusteContable) return [];

    const notas = [];
    const abonoRegistrado = parseFloat(conciliacion.monto_abono_pendiente || 0);
    const revertida = conciliacion.estado === "REVERTIDO";
    const destino = DESTINO_ABONO[conciliacion.estado_abono];

    if (!revertida && abonoRegistrado > 0 && destino) {
      notas.push(`El abono de ${formatearMonto(abonoRegistrado)} ${destino}.`);
    }

    // Revertir deja las facturas y las líneas donde estaban pero pone el abono
    // en cero, así que ahí la diferencia contra el cálculo es esperada.
    if (revertida && reparto.remanente > TOLERANCIA_REPARTO) {
      notas.push(
        "La reversión dejó el abono en cero; el remanente de arriba es el del reparto original.",
      );
    }

    if (
      !revertida &&
      Math.abs(abonoRegistrado - Math.max(reparto.remanente, 0)) >
        TOLERANCIA_REPARTO
    ) {
      notas.push(
        `El abono registrado es ${formatearMonto(
          abonoRegistrado,
        )} y no coincide con el remanente que suman estas líneas.`,
      );
    }

    return notas;
  }, [
    tieneAjusteContable,
    conciliacion.monto_abono_pendiente,
    conciliacion.estado,
    conciliacion.estado_abono,
    reparto,
  ]);

  if (!esConciliacionValida) {
    return null;
  }

  const handleClose = () => {
    onClose();
  };

  // Insignia con la naturaleza declarada para una línea. Va en cada una y no
  // una vez por conciliación: es lo que permite ver de un vistazo que dentro
  // del mismo movimiento una entró con el pago y otra se retuvo antes.
  const renderInsigniaNaturaleza = (linea) => {
    const naturaleza = naturalezaDeLinea(linea);
    const estilo = estiloNaturaleza(naturaleza);

    return (
      <Chip
        label={etiquetaNaturaleza(naturaleza)}
        size="small"
        style={{
          backgroundColor: estilo.bgColor,
          color: estilo.color,
          fontWeight: 600,
        }}
      />
    );
  };

  // Los dos subtotales, cada uno con su etiqueta. Reemplazan al total único que
  // se rotulaba como retención: con líneas de los dos signos esa cifra no
  // describe ningún monto real. Un subtotal en cero no se pinta porque no
  // aporta nada y alarga la lectura.
  const renderSubtotalesNaturaleza = (variantMonto = "h6") => {
    const filas = [
      {
        naturaleza: NATURALEZA_LINEA.ADITIVA,
        lineas: reparto.lineasAditivas,
        monto: reparto.sumaAditivas,
      },
      {
        naturaleza: NATURALEZA_LINEA.SUSTRACTIVA,
        lineas: reparto.lineasSustractivas,
        monto: reparto.sumaSustractivas,
      },
    ].filter((fila) => fila.monto > 0);

    return filas.map((fila) => (
      <Box key={fila.naturaleza} mb={1}>
        <Typography variant="caption" color="textSecondary" display="block">
          {etiquetaNaturaleza(fila.naturaleza)} ({fila.lineas.length}{" "}
          {fila.lineas.length === 1 ? "línea" : "líneas"})
        </Typography>
        <Typography
          variant={variantMonto}
          style={{
            fontWeight: 700,
            color: estiloNaturaleza(fila.naturaleza).color,
          }}
        >
          {formatearMonto(fila.monto)}
        </Typography>
      </Box>
    ));
  };

  // Desglose del reparto, en la misma forma que el modal de conciliación
  // manual, para que el operador lea lo mismo antes y después de guardar.
  // Reconstruye primero el dinero que hay que explicar --lo depositado más lo
  // que se retuvo antes de depositarlo-- para que el único signo + quede
  // aislado en su sección y de ahí hacia abajo todo reste. Una línea rotulada
  // "retenido antes del depósito" que apareciera sumando en medio de la cuenta
  // se lee como un error de captura.
  const renderDesgloseReparto = () => {
    // Con una sola línea el nombre de la categoría dice más que el conteo; con
    // varias, el conteo evita una fila por cada una.
    const detalleGrupo = (grupo) =>
      grupo.length === 1
        ? grupo[0].nombre || `ID: ${grupo[0].id}`
        : `${grupo.length} líneas`;

    // Una fila cuyo monto es 0 no aporta nada al reparto.
    const filasFacturas = detallesReparto
      .map((detalle, idx) => ({
        clave: `factura-${idx}`,
        etiqueta: `Factura ${detalle.folio}`,
        monto: parseFloat(detalle.monto_asignado) || 0,
      }))
      .filter((fila) => fila.monto > 0);

    const hayRetencion = reparto.sumaSustractivas > 0;
    const remanente = reparto.remanente;

    const filaDesglose = (clave, signo, etiqueta, monto, color) => (
      <Box key={clave} display="flex" alignItems="baseline" mb={0.5}>
        <Typography
          variant="body2"
          style={{
            width: 16,
            flexShrink: 0,
            fontWeight: 700,
            color: signo === "+" ? "#2E7D32" : "#616161",
          }}
        >
          {signo}
        </Typography>
        <Typography
          variant="body2"
          style={{ flexGrow: 1, color: color || "inherit" }}
        >
          {etiqueta}
        </Typography>
        <Typography
          variant="body2"
          style={{
            marginLeft: 16,
            fontWeight: 600,
            whiteSpace: "nowrap",
            color: color || "inherit",
          }}
        >
          {monto === null ? "" : formatearMonto(monto)}
        </Typography>
      </Box>
    );

    let cierre;
    if (remanente > TOLERANCIA_REPARTO) {
      cierre = filaDesglose(
        "cierre",
        "=",
        "Remanente, queda como abono",
        remanente,
        "#1976D2",
      );
    } else if (remanente < -TOLERANCIA_REPARTO) {
      cierre = filaDesglose(
        "cierre",
        "=",
        "Lo repartido excede el total a repartir",
        Math.abs(remanente),
        "#D32F2F",
      );
    } else {
      cierre = filaDesglose(
        "cierre",
        "=",
        "Cuadra exacto, sin remanente",
        null,
        "#2E7D32",
      );
    }

    return (
      <Card
        variant="outlined"
        style={{
          padding: 16,
          backgroundColor: "#fff",
          border: `2px solid ${ESTILO_CATEGORIZACION.borderColor}`,
        }}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginBottom: 8 }}
        >
          CÓMO SE REPARTIÓ EL MOVIMIENTO
        </Typography>

        {filaDesglose(
          "deposito",
          "",
          "Depósito recibido",
          conciliacion.movimiento?.monto,
        )}

        {/* Sin retenciones el depósito ya es el total a repartir, y repetirlo
            en dos filas iguales solo confunde. */}
        {hayRetencion && (
          <>
            {filaDesglose(
              "retenido",
              "+",
              `${etiquetaNaturaleza(
                NATURALEZA_LINEA.SUSTRACTIVA,
              )} · ${detalleGrupo(reparto.lineasSustractivas)}`,
              reparto.sumaSustractivas,
            )}
            <Divider style={{ margin: "6px 0" }} />
            {filaDesglose(
              "total",
              "=",
              "Total a repartir",
              reparto.totalARepartir,
            )}
          </>
        )}

        {filasFacturas.map((fila) =>
          filaDesglose(fila.clave, "−", fila.etiqueta, fila.monto),
        )}

        {reparto.sumaAditivas > 0 &&
          filaDesglose(
            "aditivas",
            "−",
            `${etiquetaNaturaleza(NATURALEZA_LINEA.ADITIVA)} · ${detalleGrupo(
              reparto.lineasAditivas,
            )}`,
            reparto.sumaAditivas,
          )}

        <Divider style={{ margin: "6px 0" }} />
        {cierre}

        {notasAbono.map((nota) => (
          <Typography
            key={nota}
            variant="caption"
            color="textSecondary"
            style={{ display: "block", marginTop: 8 }}
          >
            {nota}
          </Typography>
        ))}
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      {/* HEADER */}
      <DialogTitle className={classes.dialogTitle}>
        <Box>
          <Typography variant="h6">Detalle de Conciliación</Typography>
          <Typography variant="caption">
            ID: {conciliacion.id} | Fecha:{" "}
            {formatearFechaDisplay(conciliacion.fecha_concilia, true)}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent dividers>
        {/* ==================== INFORMACIÓN GENERAL ==================== */}
        <Box className={classes.section}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Información General
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="textSecondary">
                    Tipo de Conciliación
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      label={tipoConfig.label}
                      style={{
                        backgroundColor: tipoConfig.bgcolor,
                        color: tipoConfig.color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="textSecondary">
                    Estado
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      label={estadoConfig.label}
                      color={estadoConfig.color}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="textSecondary">
                    Score de Match
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="h6">
                      {conciliacion.score || "-"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="textSecondary">
                    Conciliado por
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="h6">
                      {capitalizarPalabras(conciliacion.conciliado_por) || "-"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {conciliacion.observaciones && (
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                Observaciones:
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                {conciliacion.observaciones}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* ==================== MOVIMIENTO BANCARIO ==================== */}
        <Box className={classes.section} mt={3}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Movimiento Bancario
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {bancoConfig && (
                      <Chip
                        label={bancoConfig.label}
                        size="small"
                        style={{
                          backgroundColor: bancoConfig.color,
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <Typography variant="h6">
                      {formatearMonto(conciliacion.movimiento?.monto)}
                    </Typography>
                    <Chip
                      label={conciliacion.movimiento?.tipo}
                      size="small"
                      color={
                        conciliacion.movimiento?.tipo === "INGRESO"
                          ? "success"
                          : "error"
                      }
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Fecha Movimiento
                  </Typography>
                  <Typography variant="body2">
                    {formatearFechaDisplay(conciliacion.movimiento?.fecha_mov)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    RUT Ordenante
                  </Typography>
                  <Typography variant="body2">
                    {formatearRutDisplay(
                      conciliacion.movimiento?.rut_ordenante,
                    ) || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Glosa
                  </Typography>
                  <Typography variant="body2">
                    {conciliacion.movimiento?.glosa || "-"}
                  </Typography>
                </Grid>
                {conciliacion.movimiento?.nombre_ordenante && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Nombre Ordenante
                    </Typography>
                    <Typography variant="body2">
                      {conciliacion.movimiento.nombre_ordenante}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Box>

        <Divider />

        {/* ==================== FACTURAS ASIGNADAS ==================== */}
        <Box className={classes.section} mt={3}>
          {/* Solo mostrar facturas si NO es retención completa */}
          {!esRetencionCompleta && conciliacion.detalles?.length > 0 && (
            <>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Facturas Asignadas ({conciliacion.detalles?.length || 0})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Folio</TableCell>
                      <TableCell align="right">Monto Asignado</TableCell>
                      <TableCell align="center">Tipo Asignación</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conciliacion.detalles?.map((detalle, index) => {
                      const tipoAsignacionConfig = getTipoAsignacionConfig(
                        detalle.tipo_asignacion,
                      );
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip
                              label={detalle.tipo_origen}
                              size="small"
                              color={
                                detalle.tipo_origen === "PLAN"
                                  ? "primary"
                                  : "secondary"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              #{detalle.folio}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatearMonto(detalle.monto_asignado)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={tipoAsignacionConfig.label}
                              size="small"
                              color={tipoAsignacionConfig.color}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* FILA DE TOTALES */}
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" fontWeight={700}>
                          TOTAL ASIGNADO
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          {formatearMonto(totalAsignado)}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* ==================== RETENCIÓN COMPLETA (SIN FACTURAS) ==================== */}
          {esRetencionCompleta && (
            <>
              <Divider />

              <Box className={classes.section} mt={3}>
                <Alert
                  severity="info"
                  icon={false}
                  style={{
                    backgroundColor: "#E3F2FD",
                    border: "2px solid #64B5F6",
                    borderRadius: 8,
                  }}
                >
                  <Box mb={2}>
                    <Typography
                      variant="h6"
                      style={{
                        color: "#1976D2",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      🏦 Retención Completa (Sin Facturas Asociadas)
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: "#1565C0", marginTop: 8 }}
                    >
                      Este movimiento bancario corresponde 100% a una retención
                      contable sin facturas vinculadas.
                    </Typography>
                  </Box>

                  <Divider style={{ margin: "16px 0" }} />

                  <Grid container spacing={2}>
                    {/* Subtotales por naturaleza */}
                    <Grid item xs={12}>
                      <Card
                        variant="outlined"
                        style={{
                          padding: 16,
                          backgroundColor: "#fff",
                          border: "1px solid #64B5F6",
                        }}
                      >
                        {renderSubtotalesNaturaleza()}
                      </Card>
                    </Grid>

                    {/* Lista de categorías */}
                    {categorias.map((cat, idx) => (
                      <Grid item xs={12} key={`retcat-${idx}`}>
                        <Card
                          variant="outlined"
                          style={{
                            padding: 16,
                            backgroundColor: "#fff",
                            border: "1px solid #64B5F6",
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={7}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                📁 Categoría
                              </Typography>
                              <Typography
                                variant="body1"
                                style={{
                                  fontWeight: 600,
                                  color: "#1976D2",
                                  marginTop: 4,
                                }}
                              >
                                {cat.nombre || `ID: ${cat.id}`}
                              </Typography>
                              {cat.codigo && (
                                <Typography
                                  variant="caption"
                                  style={{ color: "#1565C0" }}
                                >
                                  Código: {cat.codigo}
                                </Typography>
                              )}
                              <Box mt={1}>{renderInsigniaNaturaleza(cat)}</Box>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                💵 Monto
                              </Typography>
                              <Typography
                                variant="h6"
                                style={{
                                  fontWeight: 700,
                                  color: "#1976D2",
                                  marginTop: 4,
                                }}
                              >
                                {formatearMonto(cat.monto)}
                              </Typography>
                            </Grid>
                            {cat.observaciones && (
                              <Grid item xs={12}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                >
                                  📝 Observaciones
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontStyle: "italic",
                                    color: "#424242",
                                    lineHeight: 1.6,
                                  }}
                                >
                                  "{cat.observaciones}"
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Card>
                      </Grid>
                    ))}

                    {/* Desglose del reparto */}
                    <Grid item xs={12}>
                      {renderDesgloseReparto()}
                    </Grid>

                    {/* Nota Informativa */}
                    <Grid item xs={12}>
                      <Card
                        variant="outlined"
                        style={{
                          padding: 16,
                          backgroundColor: "#F5F5F5",
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            color: "#616161",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 18 }}>⚠️</span>
                          <span>
                            <strong>Nota:</strong> Este movimiento no tiene
                            facturas asociadas. El monto completo fue registrado
                            como categorización contable (impuestos,
                            contribuciones, reembolsos, multas, etc.).
                          </span>
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Alert>
              </Box>
            </>
          )}

          {/* ==================== AJUSTE CONTABLE (SI APLICA) ==================== */}
          {tieneAjusteContable && !esRetencionCompleta && (
            <>
              <Divider style={{ margin: "24px 0" }} />
              <Box className={classes.section}>
                <Typography
                  variant="subtitle1"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  🧾 Ajuste Contable
                </Typography>

                <Alert
                  severity="info"
                  icon={false}
                  style={{
                    backgroundColor: ESTILO_CATEGORIZACION.bgColor,
                    border: `2px solid ${ESTILO_CATEGORIZACION.borderColor}`,
                    borderRadius: 8,
                  }}
                >
                  {/* Encabezado del ajuste */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      style={{
                        fontSize: 32,
                        marginRight: 12,
                      }}
                    >
                      🧾
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        style={{
                          fontWeight: 700,
                          color: ESTILO_CATEGORIZACION.color,
                        }}
                      >
                        Categorización contable
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Cada línea declara si su monto ingresó junto con el pago
                        o si fue retenido antes del depósito.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Grid con información del ajuste */}
                  <Grid container spacing={2}>
                    {/* Subtotales por naturaleza */}
                    <Grid item xs={12}>
                      <Card
                        variant="outlined"
                        style={{ padding: 12, backgroundColor: "#fff" }}
                      >
                        {renderSubtotalesNaturaleza()}
                      </Card>
                    </Grid>

                    {/* Lista de líneas de categorización */}
                    {categorias.map((cat, idx) => (
                      <Grid item xs={12} key={`cat-${idx}`}>
                        <Card
                          variant="outlined"
                          style={{ padding: 12, backgroundColor: "#fff" }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={7}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                📁 Categoría
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                              >
                                {cat.nombre || `ID: ${cat.id}`}
                              </Typography>
                              {cat.codigo && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Código: {cat.codigo}
                                </Typography>
                              )}
                              <Box mt={1}>{renderInsigniaNaturaleza(cat)}</Box>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                💵 Monto
                              </Typography>
                              <Typography
                                variant="body1"
                                style={{
                                  fontWeight: 700,
                                  color: ESTILO_CATEGORIZACION.color,
                                }}
                              >
                                {formatearMonto(cat.monto)}
                              </Typography>
                            </Grid>
                            {cat.observaciones && (
                              <Grid item xs={12}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                >
                                  📝 Observaciones
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontStyle: "italic" }}
                                >
                                  "{cat.observaciones}"
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Card>
                      </Grid>
                    ))}

                    {/* Desglose del reparto */}
                    <Grid item xs={12}>
                      {renderDesgloseReparto()}
                    </Grid>
                  </Grid>
                </Alert>
              </Box>
            </>
          )}

          {/* RESUMEN DE MONTOS */}
          <Box mt={2}>
            <Card
              variant="outlined"
              style={{
                backgroundColor: tieneAjusteContable
                  ? "#E3F2FD" // Azul suave si hay categorías
                  : Math.abs(diferencia) < 0.01
                    ? "#E8F5E9" // Verde si es exacto
                    : "#FFF3E0", // Naranja si hay diferencia
              }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary">
                      Monto Movimiento
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatearMonto(conciliacion.movimiento?.monto)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary">
                      Total Asignado
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatearMonto(totalAsignado)}
                    </Typography>
                  </Grid>
                  {/* Con líneas de categoría este recuadro mostraba una sola
                      cifra rotulada como retención o comisión. Esa cifra sumaba
                      las líneas sin mirar su signo, así que con signos mixtos no
                      describía ningún monto real: ahora van los dos subtotales
                      por separado. */}
                  <Grid item xs={4}>
                    {tieneAjusteContable ? (
                      renderSubtotalesNaturaleza("body2")
                    ) : (
                      <>
                        <Typography variant="caption" color="textSecondary">
                          Diferencia
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={
                            Math.abs(diferencia) < 0.01 ? "success" : "warning"
                          }
                        >
                          {formatearMonto(Math.abs(diferencia))}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* ==================== INFORMACIÓN ADICIONAL ==================== */}
        {conciliacion.razon_json && (
          <>
            <Divider />
            <Box className={classes.section} mt={3}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Detalles del Matching
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <pre
                    style={{
                      fontSize: 11,
                      overflow: "auto",
                      maxHeight: 200,
                      backgroundColor: "#f5f5f5",
                      padding: 8,
                      borderRadius: 4,
                    }}
                  >
                    {JSON.stringify(conciliacion.razon_json, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
